import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Download, FileCode2, FileDown } from "lucide-react";
import { api, download, eur, dt } from "@/lib/api";
import { PageHeader, Field, Empty } from "@/components/Common";
import { StatusBadge } from "@/components/StatusBadge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DateField } from "@/components/DateField";

const ALL = "all";

export default function StudioArchive() {
  const [sp] = useSearchParams();
  const [companies, setCompanies] = useState([]);
  const [items, setItems] = useState(null);
  const [f, setF] = useState({ company_id: sp.get("company") || ALL, direction: ALL, date_from: `${new Date().getFullYear()}-01-01`, date_to: "" });

  useEffect(() => { api.get("/studio/overview").then((r) => setCompanies(r.data.companies)); }, []);
  const params = () => ({ company_id: f.company_id === ALL ? undefined : f.company_id, direction: f.direction === ALL ? undefined : f.direction,
    date_from: f.date_from || undefined, date_to: f.date_to || undefined });
  const load = useCallback(() => {
    api.get("/studio/invoices", { params: params() }).then((r) => setItems(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f]);
  useEffect(() => { load(); }, [load]);

  const dl = (path, p) => download(path, p).catch((e) => toast.error(e.message));

  return (
    <div>
      <PageHeader title="Archivio XML" subtitle="Fatture emesse e ricevute delle aziende abbinate, pronte per la contabilità">
        <Button data-testid="export-zip-button" className="bg-blue-600 hover:bg-blue-700" onClick={() => dl("/studio/export", params())}><Download className="mr-2 h-4 w-4" />Scarica ZIP</Button>
      </PageHeader>
      <div className="mb-5 grid gap-3 rounded-xl border border-slate-200 bg-white p-4 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="Azienda">
          <Select value={f.company_id} onValueChange={(v) => setF({ ...f, company_id: v })}>
            <SelectTrigger data-testid="archive-company-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value={ALL}>Tutte le aziende</SelectItem>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.denominazione}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Tipo">
          <Select value={f.direction} onValueChange={(v) => setF({ ...f, direction: v })}>
            <SelectTrigger data-testid="archive-direction-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value={ALL}>Emesse e ricevute</SelectItem><SelectItem value="emessa">Solo emesse</SelectItem><SelectItem value="ricevuta">Solo ricevute</SelectItem></SelectContent>
          </Select>
        </Field>
        <Field label="Dal"><DateField clearable testid="archive-from-input" value={f.date_from} onChange={(v) => setF({ ...f, date_from: v })} /></Field>
        <Field label="Al"><DateField clearable testid="archive-to-input" value={f.date_to} onChange={(v) => setF({ ...f, date_to: v })} /></Field>
      </div>
      {items && items.length === 0 ? <Empty text="Nessun XML per i filtri selezionati" /> : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <Table data-testid="archive-table">
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead>Azienda</TableHead><TableHead>Tipo</TableHead><TableHead>Numero</TableHead><TableHead>Data</TableHead><TableHead>Controparte</TableHead><TableHead className="text-right">Totale</TableHead><TableHead>Stato</TableHead><TableHead />
            </TableRow></TableHeader>
            <TableBody>
              {(items || []).map((i) => (
                <TableRow key={i.id} data-testid={`archive-row-${i.id}`}>
                  <TableCell className="max-w-[180px] truncate text-sm">{i.company_name}</TableCell>
                  <TableCell><span className={`rounded px-2 py-0.5 text-xs font-semibold ${i.direction === "emessa" ? "bg-sky-50 text-sky-700" : "bg-indigo-50 text-indigo-700"}`}>{i.direction === "emessa" ? "Emessa" : "Ricevuta"} {i.tipo}</span></TableCell>
                  <TableCell className="font-mono text-sm">{i.numero}</TableCell>
                  <TableCell className="text-sm">{dt(i.data)}</TableCell>
                  <TableCell className="max-w-[200px] truncate text-sm">{i.controparte.denominazione}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{eur(i.totals.totale)}</TableCell>
                  <TableCell><StatusBadge status={i.sdi_status} /></TableCell>
                  <TableCell className="whitespace-nowrap text-right">
                    <Button variant="ghost" size="icon" title="XML" data-testid={`archive-xml-${i.id}`} onClick={() => dl(`/studio/invoices/${i.id}/xml`)}><FileCode2 className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" title="PDF" data-testid={`archive-pdf-${i.id}`} onClick={() => dl(`/studio/invoices/${i.id}/pdf`)}><FileDown className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
